import { useEffect, useRef } from 'react';

const steps = [
  {
    number: '01',
    title: 'Diagnóstico Inicial',
    description:
      'Nos sentamos contigo para entender tu operación, tus retos diarios y qué tareas te quitan más tiempo.',
  },
  {
    number: '02',
    title: 'Estrategia Clara',
    description:
      'Te presentamos un plan con resultados esperados, usando herramientas simples que tú y tu equipo puedan adoptar sin fricción.',
  },
  {
    number: '03',
    title: 'Implementación',
    description:
      'Construimos e integramos el sistema en tu día a día. El cambio es invisible para tus clientes, pero inmediato para tu equipo.',
  },
  {
    number: '04',
    title: 'Soporte y Evolución',
    description:
      'Monitoreamos y ajustamos tus sistemas con el tiempo. Tu inversión en IA no se queda estática — mejora con tu negocio.',
  },
];

export default function HowItWorks() {
  const sectionRef = useRef<HTMLElement>(null);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('opacity-100', 'translate-y-0');
            entry.target.classList.remove('opacity-0', 'translate-y-8');
          }
        });
      },
      { threshold: 0.15 }
    );

    const headerEl = sectionRef.current?.querySelector('.section-header');
    if (headerEl) observer.observe(headerEl);
    itemRefs.current.forEach((el) => {
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="process"
      ref={sectionRef}
      className="relative overflow-hidden"
      style={{ background: '#0D0D1A', scrollMarginTop: '80px', paddingTop: '60px', paddingBottom: '60px' }}
    >
      {/* Atmospheric glow */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_50%_at_50%_100%,rgba(123,63,228,0.07)_0%,transparent_70%)]" />
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[800px] h-[1px] bg-gradient-to-r from-transparent via-[#7B3FE4]/30 to-transparent" />

      <div className="relative z-10 max-w-[1400px] mx-auto px-4 sm:px-8">

        {/* Section Header */}
        <div className="section-header mb-14 opacity-0 translate-y-8 transition-all duration-700 ease-out">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-5 h-[2px] bg-[#7B3FE4]" />
            <span
              className="text-[#7B3FE4] uppercase"
              style={{ fontFamily: 'JetBrains Mono, Courier New, monospace', fontSize: '13px', letterSpacing: '0.2em' }}
            >
              Cómo trabajamos
            </span>
          </div>
          <h2
            className="text-4xl font-bold text-[#F0EFF8] leading-tight"
            style={{ fontFamily: 'Sora, sans-serif', letterSpacing: '-0.02em' }}
          >
            Un proceso claro,<br />
            <span className="text-[#7B3FE4]">resultados medibles.</span>
          </h2>
        </div>

        {/* Steps */}
        <div className="relative">

          {/* Connector line — more visible */}
          <div className="hidden lg:block absolute top-[52px] left-[calc(12.5%)] right-[calc(12.5%)] h-[2px] z-0">
            <div className="w-full h-full bg-gradient-to-r from-[#7B3FE4]/30 via-[#7B3FE4]/70 to-[#7B3FE4]/30" />
            <div
              className="absolute inset-0 h-full"
              style={{
                background: 'linear-gradient(90deg, transparent 0%, #7B3FE4 50%, transparent 100%)',
                animation: 'flowLine 3s linear infinite',
                backgroundSize: '200% 100%',
              }}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {steps.map((step, index) => (
              <div
                key={index}
                ref={(el) => { itemRefs.current[index] = el; }}
                className="group opacity-0 translate-y-8 transition-all duration-700 ease-out flex flex-col"
                style={{ transitionDelay: `${index * 120}ms` }}
              >
                {/* Large bold number — top anchor for the connector line */}
                <div className="flex justify-center mb-6 relative z-10">
                  <span
                    className="font-bold leading-none select-none"
                    style={{
                      fontFamily: 'Sora, sans-serif',
                      fontSize: '56px',
                      color: '#7B3FE4',
                      letterSpacing: '-0.03em',
                      textShadow: '0 0 30px rgba(123,63,228,0.4)',
                    }}
                  >
                    {step.number}
                  </span>
                </div>

                {/* Card */}
                <div
                  className="flex-1 p-6 border border-[rgba(255,255,255,0.06)] group-hover:border-[#7B3FE4]/40 transition-all duration-300 relative overflow-hidden"
                  style={{
                    background: 'rgba(13,13,26,0.8)',
                    borderRadius: '2px',
                  }}
                >
                  {/* Hover glow */}
                  <div
                    className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
                    style={{
                      background:
                        'radial-gradient(ellipse at top left, rgba(123,63,228,0.06) 0%, transparent 70%)',
                    }}
                  />

                  {/* Title */}
                  <h3
                    className="text-[#F0EFF8] mb-3 leading-snug group-hover:text-white transition-colors duration-200"
                    style={{ fontFamily: 'Sora, sans-serif', fontSize: '20px', fontWeight: 700, letterSpacing: '-0.01em' }}
                  >
                    {step.title}
                  </h3>

                  {/* Description */}
                  <p
                    className="text-[#8887A0]"
                    style={{ fontFamily: 'Sora, sans-serif', fontSize: '16px', lineHeight: '1.75' }}
                  >
                    {step.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes flowLine {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
      `}</style>
    </section>
  );
}